import React from 'react';
import { array, bool, func, object, string } from 'prop-types';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage } from '../../../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../../../util/types';

// Import shared components
import { H3, ListingLink } from '../../../../components';

// Import modules from this directory
import EditListingPhotosForm from './EditListingPhotosForm';
import css from './EditListingPhotosPanel.module.css';
import { shiftToZeroIndex } from '../../../../util/data';

const getInitialValues = params => {
  const {
    images,
    listing: {
      attributes: { publicData, state },
    },
  } = params || {};
  const imageDescription = publicData?.imageDescription || {};

  let updatedImageDescriptions = {};
  images.forEach(image => {
    updatedImageDescriptions[image.id.uuid] = imageDescription?.[image.id.uuid] || '';
  });

  return { images, ...updatedImageDescriptions, ...publicData, state  };
};

const EditListingPhotosPanel = props => {
  const {
    className,
    rootClassName,
    errors,
    disabled,
    ready,
    listing,
    onImageUpload,
    submitButtonText,
    draftButtonText,
    panelUpdated,
    updateInProgress,
    saveAsDraftInProgress,
    onSubmit,
    onRemoveImage,
    listingImageConfig,
  } = props;

  const rootClass = rootClassName || css.root;
  const classes = classNames(rootClass, className);
  const isPublished = listing?.id && listing?.attributes?.state !== LISTING_STATE_DRAFT;

  return (
    <div className={classes}>
      <H3 as="h1">
        {isPublished ? (
          <FormattedMessage
            id="EditListingPhotosPanel.title"
            values={{ listingTitle: <ListingLink listing={listing} />, lineBreak: <br /> }}
          />
        ) : (
          <FormattedMessage
            id="EditListingPhotosPanel.createListingTitle"
            values={{ lineBreak: <br /> }}
          />
        )}
      </H3>
      <EditListingPhotosForm
        className={css.form}
        disabled={disabled}
        ready={ready}
        fetchErrors={errors}
        initialValues={getInitialValues(props)}
        onImageUpload={onImageUpload}
        onSubmit={values => {
          const { addImage, images, id, primaryImageId, saveDraftOnly } = values;
          const imageIndex = images.findIndex(elm => (elm?.imageId?.uuid ?? elm.id.uuid) === primaryImageId);

          let imageDescription = {};
          images.forEach(elm => {
            const imageId = elm?.imageId?.uuid ?? elm?.id?.uuid ?? null;

            if (imageId) {
              imageDescription = {
                ...imageDescription,
                [imageId]: values[imageId],
              };
            }
          });

          let imagesArray = [...images];
          if (imageIndex > -1) {
            imagesArray = shiftToZeroIndex(imagesArray, imageIndex);
          }
          const updateValues = {
            images: imagesArray,
            publicData: {
              imageDescription,
              primaryImageId: primaryImageId && imageIndex !== -1
                ? primaryImageId
                : images?.[0]?.imageId?.uuid ?? images?.[0]?.id?.uuid ?? null,
            },
          };

          onSubmit(updateValues, saveDraftOnly);
        }}
        onRemoveImage={onRemoveImage}
        saveActionMsg={submitButtonText}
        draftButtonText={draftButtonText}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        saveAsDraftInProgress={saveAsDraftInProgress}
        listingImageConfig={listingImageConfig}
      />
    </div>
  );
};

EditListingPhotosPanel.defaultProps = {
  className: null,
  rootClassName: null,
  errors: null,
  images: [],
  listing: null,
};

EditListingPhotosPanel.propTypes = {
  className: string,
  rootClassName: string,
  errors: object,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  images: array,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  onImageUpload: func.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  onRemoveImage: func.isRequired,
  listingImageConfig: object.isRequired,
};

export default EditListingPhotosPanel;
